/**
 * E7 PR 0b (owner, 2026-10-20) — THE one "the session ended" primitive. Nothing calls it directly but a few NAMED
 * helpers, each ending the session synchronously right before the client acts on the change:
 *  - sign-out — useAuth's `signOut` (and a sign-out in another tab);
 *  - the signed-in user changing (a session that died, another account) — useAuth's `setSessionUser`;
 *  - a tenant / identity switch the server CONFIRMED — useTenant's `applySwitch` (a refused one keeps the session);
 *  - leaving the share view or moving to another share session — `pages/share/shareLeave.ts`.
 * `sessionEnded.test.ts` is structural: it fails on a direct `endSession` anywhere else, and on any use of the
 * primitives these helpers wrap (the auth sign-out, `setUser`, the switch refs, a navigation to a share front door or
 * another share session) outside them.
 *
 * Work that belongs to the session reads `sessionSignal()` when it starts and passes it to its request (aborting it in
 * flight) — and drops any answer that lands after the session ended: no toast, no state update. The Bin restore (drive,
 * share view, undo) does it by hand; since #1316 EVERY api-client `request()` / `fetchRaw` / `sessionFetch` does it
 * too (the getter below — the request is not aborted; its late answer rejects with api-client's `SessionEndedError`,
 * every `finally` runs, no success path does, and toasts raised while it is handled are refused), and the toast
 * provider clears what is on screen when a session ends.
 */
import { setAccountGetter, setSessionSignalGetter } from '@papan/api-client'

let controller = new AbortController()
const listeners = new Set<(reason: SessionEndReason) => void>()
/** Bumped when the signed-in ACCOUNT may have changed (sign-out, another user, a dead session) — never on a switch of
 *  tenant / identity within the same account. */
let account = 0
export const ACCOUNT_REASONS: ReadonlySet<SessionEndReason> = new Set<SessionEndReason>(['signed-out', 'signed-out-elsewhere', 'user-changed', 'session-died'])

/** #1316 — the ends after which NOTHING of the old session's UI may stay mounted: the account may have changed, or the
 *  identity within the tenant did (a tenant switch remounts through its route; a share end through its own view). */
export const EPOCH_REASONS: ReadonlySet<SessionEndReason> = new Set<SessionEndReason>([...ACCOUNT_REASONS, 'identity-switched'])
let epoch = 0
const epochListeners = new Set<() => void>()

/** #1316 — the session epoch `SessionScope` keys on: bumped by every `EPOCH_REASONS` end. */
export function sessionEpoch(): number {
  return epoch
}

/** Called after each epoch bump (a `useSyncExternalStore` subscribe — a stable function); returns the unsubscribe. */
export function onSessionEpochChange(fn: () => void): () => void {
  epochListeners.add(fn)
  return () => { epochListeners.delete(fn) }
}

/** D134 — which signed-in account work belongs to: compare a value read when the work was recorded with the one now. */
export function accountEpoch(): number {
  return account
}
// the api-client stamps it on every captured identity (`isCurrentIdentity` compares it); a test that mocks the
// api-client without it just runs without the stamp
try { setAccountGetter(accountEpoch) } catch { /* mocked api-client */ }
// #1316: every api-client request / fetchRaw / sessionFetch reads it when it starts and drops a later answer
try { setSessionSignalGetter(() => controller.signal) } catch { /* mocked api-client */ }

export type SessionEndReason =
  | 'signed-out' | 'signed-out-elsewhere' | 'user-changed' | 'tenant-switched' | 'identity-switched'
  | 'session-died' | 'share-signed-out' | 'share-revoked' | 'share-identity-switched' | 'share-session-replaced'
  | 'share-session-lapsed' | 'share-left' | 'session-context-changed'

/** #1316 review round 2 — the ends after which the share view no longer reads the share session it read: it moves to
 *  ANOTHER share session (an identity switch, a replaced session — its `activeSid` / `bodySid` lag until the new one's
 *  `/info` or sub-session lands), or leaves the view (a Log out, a lapse's re-entry, the visitor's own drive, a route
 *  move). Every other end KEEPS the share session it read (a sign-out in another tab for an unbound visit, a revoke the
 *  server did not confirm, a drive tenant / identity switch): the view stays on it. */
const SHARE_SESSION_MOVES: ReadonlySet<SessionEndReason> = new Set<SessionEndReason>([
  'share-identity-switched', 'share-session-replaced', 'share-signed-out', 'share-session-lapsed', 'share-left',
  'session-context-changed',
])
export function endMovesShareSession(reason: SessionEndReason): boolean {
  return SHARE_SESSION_MOVES.has(reason)
}
let lastEnd: SessionEndReason | null = null
/** Whether the most recent end moved the share view off the share session it read (`endMovesShareSession`). */
export function lastEndMovedShareSession(): boolean {
  return lastEnd !== null && SHARE_SESSION_MOVES.has(lastEnd)
}

/** The current session's signal: live until `endSession`, then aborted (an `AbortError`). Read it when work starts. */
export function sessionSignal(): AbortSignal {
  return controller.signal
}

/** The session ended: abort its signal (in-flight requests included), tell the listeners, start a fresh one.
 *  A listener runs BEFORE the caller applies what comes next (the new tenant / identity headers, the sign-out's cookie
 *  clear): it must not send a request synchronously — that request would go out as the OLD session yet be bound to
 *  the new signal, so its answer is kept (#1316). */
export function endSession(reason: SessionEndReason): void {
  const ended = controller
  controller = new AbortController()
  if (ACCOUNT_REASONS.has(reason)) account++
  lastEnd = reason
  ended.abort(new DOMException(`The session ended (${reason}).`, 'AbortError'))
  // #1316: the epoch moves before the end listeners run (they read the new one), its subscribers hear it after them
  const bumped = EPOCH_REASONS.has(reason)
  if (bumped) epoch++
  for (const fn of [...listeners]) {
    try { fn(reason) } catch { /* one listener's failure never stops the others */ }
  }
  if (bumped) for (const fn of [...epochListeners]) {
    try { fn() } catch { /* as above */ }
  }
}

// ── The route's session context (the backstop) ─────────────────────────────────────────────────────────────────────
// A share-view session and the drive can resolve to the SAME tenant (an anonymous / personal visitor), so a tenant
// change alone misses Back from /share-view/<sid> to the drive. The route's context — which share session, or which
// tenant's drive — is watched instead (useTenant): any change from a known context ends the session, unless the
// helper that made the move already accounted for it (its own end, or a same-identity sub-session).

let accounted: { next: string; until: number } | null = null
const ACCOUNTED_WINDOW_MS = 5_000

/** The route's session context: `share:<sid>` on a share view, `drive:<tenant>` elsewhere; '' = none yet (a first
 *  sign-in is not a change). */
export function sessionContextOf(pathname: string, tenantId: string | null | undefined): string {
  const m = /^\/share-view\/([^/?#]+)/.exec(pathname)
  if (m) return `share:${m[1]}`
  return tenantId ? `drive:${tenantId}` : ''
}

/** The move the caller is about to make — to exactly `next` (`sessionContextOf`; a bare kind `drive:` = any drive,
 *  when the tenant isn't known there) — is accounted for (it ended the session itself, or keeps it on purpose): a
 *  change to THAT context is not a session end. One-shot; any other change clears it (and ends the session); it
 *  lapses after a few seconds. Callers set it only when `next` differs from where the route is now. */
export function sessionContextMoveAccountedFor(next: string): void {
  accounted = { next, until: Date.now() + ACCOUNTED_WINDOW_MS }
}

/** The route moved from one session context to another (Back / Forward, a link, a route change): a session end,
 *  unless a helper accounted for the move. */
export function sessionContextChanged(prev: string, next: string): void {
  if (!prev || prev === next) return
  const mark = accounted
  accounted = null
  const expected = !!mark && Date.now() <= mark.until
    && (mark.next === next || (mark.next.endsWith(':') && next.startsWith(mark.next)))
  if (!expected) endSession('session-context-changed')
}

/** Called on every session end; returns the unsubscribe. */
export function onSessionEnd(fn: (reason: SessionEndReason) => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

let accountEndUnloading = false
/** #1316 review round 1 (G1 review I-1) — set by the web app's `reloadAfterAccountEnd` right before its full page load:
 *  the page is the old ACCOUNT's and is going away whatever it holds. A prompting `beforeunload` handler stays quiet
 *  then (`beforeUnloadPrompt`) — a "Leave site?" the person answered with Stay would keep A's page (its open editors,
 *  chat, caches) running on B's cookie (P10). Before G1 the in-app sign-out dropped unsaved edits silently; parity. */
export function markAccountEndUnloading(): void {
  accountEndUnloading = true
}
export function isAccountEndUnloading(): boolean {
  return accountEndUnloading
}
/** The page did not unload after all (no navigation started): ordinary dirty-tab prompts apply again. Also reset by
 *  tests, whose mocked `assign` never unloads the page. */
export function clearAccountEndUnloading(): void {
  accountEndUnloading = false
}
/** THE "unsaved changes — leave?" prompt every `beforeunload` handler that prompts calls: asks the browser to confirm,
 *  except during an account-end load. */
export function beforeUnloadPrompt(e: BeforeUnloadEvent): void {
  if (accountEndUnloading) return
  e.preventDefault()
}

export const resetAccountEndUnloadingForTests = clearAccountEndUnloading
